// Нейтральные чипы направлений и сервисов, бейджи статусов, значок приоритета.
// Цвет направлений (Direction.color) в интерфейсе не используется: цвет в Orbis — только для смысла.
import { PRIORITY_LABELS, STATUS_TONES, TASK_KIND_LABELS } from '../lib/format.ts'
import { useDirectionMap, useServiceMap } from '../store.ts'
import { TASK_STATUS_LABELS, type Direction, type Priority, type TaskKind, type TaskStatus } from '../types.ts'
import { Icon } from './Icon.tsx'
import { Badge, cx } from './ui.tsx'

const CHIP =
  'inline-flex h-5 max-w-full shrink-0 items-center rounded-[5px] border px-1.5 text-[11.5px] leading-none font-medium whitespace-nowrap'

export function DirectionChip({
  direction,
  id,
  className,
  onClick,
  active,
}: {
  direction?: Direction | null
  id?: string
  className?: string
  onClick?: () => void
  active?: boolean
}) {
  const map = useDirectionMap()
  const d = direction ?? (id ? map.get(id) : undefined)
  const name = d?.name ?? id ?? '—'
  const cls = cx(
    CHIP,
    active ? 'border-accent/40 bg-accent-soft text-fg' : 'border-line bg-surface-2 text-muted',
    onClick && !active && 'hover:border-line-strong hover:text-fg',
    className,
  )
  if (onClick) {
    return (
      <button type="button" className={cls} onClick={onClick} aria-pressed={active}>
        {active ? <Icon name="check" size={11} className="mr-1 text-accent" /> : null}
        <span className="truncate">{name}</span>
      </button>
    )
  }
  return (
    <span className={cls} title={d?.description || name}>
      <span className="truncate">{name}</span>
    </span>
  )
}

export function ServiceChip({ id, onClick }: { id: string; onClick?: () => void }) {
  const services = useServiceMap()
  const s = services.get(id)
  const cls = cx(CHIP, 'border-line bg-surface font-mono text-[11px] font-normal text-fg-2')
  if (onClick) {
    return (
      <button type="button" className={cx(cls, 'hover:border-line-strong hover:text-fg')} onClick={onClick} title={s?.purpose}>
        <span className="truncate">{s?.name ?? id}</span>
      </button>
    )
  }
  return (
    <span className={cls} title={s?.purpose || id}>
      <span className="truncate">{s?.name ?? id}</span>
    </span>
  )
}

export function StatusBadge({ status }: { status: TaskStatus }) {
  const tone = STATUS_TONES[status]
  return (
    <Badge tone={tone} dot={tone !== 'neutral'}>
      {TASK_STATUS_LABELS[status]}
    </Badge>
  )
}

/** Значок приоритета: только «срочно» (красный) и «высокий» (жёлтый). Обычный и низкий — без значка. */
export function PriorityIcon({ priority, className }: { priority: Priority; className?: string }) {
  if (priority !== 'urgent' && priority !== 'high') return null
  const label = `Приоритет: ${PRIORITY_LABELS[priority]}`
  return (
    <span role="img" aria-label={label} title={label} className={cx('inline-flex shrink-0', priority === 'urgent' ? 'text-danger' : 'text-warn', className)}>
      <Icon name={priority === 'urgent' ? 'chevronsUp' : 'arrowUp'} size={14} strokeWidth={2.2} />
    </span>
  )
}

/** «сквозная» для kind=cross или задачи на несколько направлений + бейдж особого вида задачи. */
export function TaskKindBadge({ kind, cross }: { kind: TaskKind; cross?: boolean }) {
  const isCross = kind === 'cross' || Boolean(cross)
  const special = kind !== 'task' && kind !== 'cross'
  return (
    <>
      {isCross ? (
        <Badge title="Задача затрагивает несколько направлений">сквозная</Badge>
      ) : null}
      {special ? <Badge tone={kind === 'incident' ? 'danger' : 'neutral'}>{TASK_KIND_LABELS[kind]}</Badge> : null}
    </>
  )
}
